import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus,
  ScanLine,
  Search,
  Trash2,
  Pencil,
  ChevronRight,
  Users,
  Stethoscope,
  ArrowUpRight,
  CheckCircle2,
  CalendarCheck,
  Sparkles,
} from "@/components/icons";
import { useAuth } from "../../lib/auth";
import { useOfficeFilter } from "../../lib/officeFilter";
import EditPatientModal from "../../components/EditPatientModal";
import NewPatientModal from "../../components/patient/NewPatientModal";
import RowActionsMenu from "../../components/RowActionsMenu";
import { TableSkeleton } from "../../components/Skeletons";
import AppleKpi from "../../components/AppleKpi";

export default function Patients() {
  const [items, setItems] = useState([]);
  const [appts, setAppts] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const { user } = useAuth();
  const { officeId } = useOfficeFilter();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filtro = searchParams.get("filtro") || "";
  const setFiltro = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (!next) sp.delete("filtro");
    else sp.set("filtro", next);
    setSearchParams(sp, { replace: true });
  };
  const isSecretary = user?.role === "secretary";
  const base = isSecretary ? "/secretaria/pacientes" : "/doctor/pacientes";

  const [startingId, setStartingId] = useState(null);

  const startConsultation = async (patient) => {
    if (isSecretary) return;
    setStartingId(patient.id);
    try {
      const now = new Date().toISOString();
      const { data: appt } = await api.post("/appointments", {
        patient_id: patient.id,
        date: now,
        duration_min: 30,
        reason: "Consulta directa",
      });
      nav(`/doctor/consulta/${appt.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo iniciar la consulta");
    } finally {
      setStartingId(null);
    }
  };

  const load = async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/patients"),
        api.get("/appointments").catch(() => ({ data: [] })),
      ]);
      setItems(p.data);
      setAppts(a.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const safeLoad = async () => {
      if (cancelled) return;
      try {
        const [p, a] = await Promise.all([
          api.get("/patients"),
          api.get("/appointments").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setItems(p.data);
        setAppts(a.data);
        setFirstLoad(false);
      } catch (err) {
        if (!cancelled) toast.error(formatErr(err.response?.data?.detail));
      }
    };
    safeLoad();
    const t = setInterval(safeLoad, 10000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar paciente?")) return;
    try {
      await api.delete(`/patients/${id}`);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const officePatientIds = new Set(
    officeId
      ? appts.filter((a) => a.office_id === officeId).map((a) => a.patient_id)
      : []
  );

  const matchesOffice = (p) =>
    !officeId ||
    !p.office_id ||
    p.office_id === officeId ||
    officePatientIds.has(p.id);

  const filtered = useMemo(() => {
    const now = new Date();
    const inLastN = (d, days) =>
      d && (now - new Date(d)) / (1000 * 60 * 60 * 24) <= days;
    const isFuture = (d) => d && new Date(d) > now;

    const apptsByPatient = new Map();
    for (const a of appts) {
      if (!apptsByPatient.has(a.patient_id)) apptsByPatient.set(a.patient_id, []);
      apptsByPatient.get(a.patient_id).push(a);
    }

    let list = items.filter(
      (p) =>
        matchesOffice(p) &&
        (!q ||
          p.full_name?.toLowerCase().includes(q.toLowerCase()) ||
          p.document_id?.includes(q) ||
          p.phone?.includes(q))
    );

    if (filtro === "activos") {
      list = list.filter((p) => {
        const l = apptsByPatient.get(p.id) || [];
        return l.some((a) => inLastN(a.date, 90) || isFuture(a.date));
      });
    } else if (filtro === "programados") {
      list = list.filter((p) => {
        const l = apptsByPatient.get(p.id) || [];
        return l.some((a) => isFuture(a.date));
      });
    } else if (filtro === "nuevos") {
      list = list.filter((p) => inLastN(p.created_at, 30));
    } else if (filtro === "ultimo") {
      list = [...list].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    } else if (filtro === "alta") {
      list = list.filter((p) => {
        const l = apptsByPatient.get(p.id) || [];
        if (l.length === 0) return false;
        return !l.some((a) => isFuture(a.date)) && !l.some((a) => inLastN(a.date, 180));
      });
    }
    return list;
  }, [items, appts, q, officeId, filtro]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastVisitById = useMemo(() => {
    const map = {};
    for (const a of appts) {
      const cur = map[a.patient_id];
      if (!cur || new Date(a.date) > new Date(cur)) map[a.patient_id] = a.date;
    }
    return map;
  }, [appts]);

  // ── KPI stats ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const now = new Date();
    const inLastN = (d, days) =>
      d && (now - new Date(d)) / (1000 * 60 * 60 * 24) <= days;
    const isFuture = (d) => d && new Date(d) > now;

    const apptsByPatient = new Map();
    for (const a of appts) {
      if (!apptsByPatient.has(a.patient_id)) apptsByPatient.set(a.patient_id, []);
      apptsByPatient.get(a.patient_id).push(a);
    }

    const applicable = items.filter(matchesOffice);
    const total = applicable.length;
    const activos = applicable.filter((p) => {
      const l = apptsByPatient.get(p.id) || [];
      return l.some((a) => inLastN(a.date, 90) || isFuture(a.date));
    }).length;
    const programados = applicable.filter((p) => {
      const l = apptsByPatient.get(p.id) || [];
      return l.some((a) => isFuture(a.date));
    }).length;
    const nuevos = applicable.filter((p) => inLastN(p.created_at, 30)).length;

    // buckets últimos 6 meses de nuevos pacientes
    const bucketsNew = Array.from({ length: 6 }, (_, i) => {
      const from = new Date();
      from.setMonth(from.getMonth() - (5 - i), 1);
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setMonth(to.getMonth() + 1);
      const count = applicable.filter(
        (p) =>
          p.created_at &&
          new Date(p.created_at) >= from &&
          new Date(p.created_at) < to
      ).length;
      return {
        count,
        label: from.toLocaleDateString("es-ES", { month: "short" }),
        isCurrent: i === 5,
      };
    });

    return { total, activos, programados, nuevos, bucketsNew };
  }, [items, appts, officeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const initials = (name) =>
    (name || "")
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const onNew = () => setNewOpen(true);
  const onNewQR = () => setNewOpen(true);

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="patients-page">
      {/* ── KPI ROW ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <TotalDonut total={stats.total} activos={stats.activos} />
        <ActivosSpectrum activos={stats.activos} total={stats.total} />
        <ProgramadosCard programados={stats.programados} total={stats.total} />
        <NuevosBars buckets={stats.bucketsNew} nuevos={stats.nuevos} />
      </div>

      {/* ── LIST CARD ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-black/[0.05] p-4 flex-1 flex flex-col min-h-0">
        <div className="flex flex-col mb-3 gap-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-semibold text-black flex items-center gap-1.5">
                <Users className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
                Lista de pacientes
              </div>
              <div className="text-[11px] text-black/45 mt-0.5">
                {filtered.length} {filtered.length === 1 ? "coincidencia" : "coincidencias"}
                {filtro && ` · filtro: ${filtro}`}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-1 sm:flex-initial sm:min-w-[280px] max-w-md">
              <div className="flex-1 bg-white border border-black/[0.08] rounded-lg flex items-center px-3 h-9 focus-within:border-black/40 transition">
                <Search className="w-4 h-4 text-black/40 flex-shrink-0" strokeWidth={1.75} weight="regular" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Nombre, documento o teléfono…"
                  className="flex-1 bg-transparent px-2 outline-none text-[13px] text-black placeholder-black/40"
                  data-testid="patients-search"
                />
              </div>
              <button
                type="button"
                onClick={onNewQR}
                data-testid="new-patient-qr-button"
                title="Escanear con QR"
                className="h-9 px-3 rounded-lg bg-white border border-black/[0.08] hover:bg-black/[0.03] text-[13px] font-semibold text-black inline-flex items-center gap-1.5 transition flex-shrink-0"
              >
                <ScanLine className="w-4 h-4" strokeWidth={1.75} weight="regular" /> QR
              </button>
              <button
                type="button"
                onClick={onNew}
                data-testid="new-patient-button"
                className="h-9 pl-2.5 pr-3 rounded-lg bg-[#0071E3] hover:bg-[#0077ED] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 transition flex-shrink-0"
              >
                <Plus className="w-4 h-4" strokeWidth={2} weight="regular" /> Nuevo paciente
              </button>
            </div>
          </div>

          {/* Chips de filtro rápido */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { k: "", label: "Todos" },
              { k: "activos", label: "Activos" },
              { k: "nuevos", label: "Nuevos" },
              { k: "programados", label: "Con cita" },
              { k: "ultimo", label: "Recientes" },
            ].map((chip) => {
              const active = filtro === chip.k;
              return (
                <button
                  key={chip.k || "all"}
                  type="button"
                  data-testid={`patients-chip-${chip.k || "all"}`}
                  onClick={() => setFiltro(chip.k)}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold transition ${
                    active
                      ? "bg-[#0071E3] text-white"
                      : "bg-white border border-black/[0.08] text-black hover:bg-black/[0.03]"
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {firstLoad ? (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar" data-testid="patients-loading">
            <TableSkeleton rows={8} />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="flex-1 flex flex-col items-center justify-center text-center py-8"
            data-testid="patients-empty"
          >
            <div className="w-10 h-10 rounded-md bg-[#F2F3F5] flex items-center justify-center mb-2">
              <Users className="w-5 h-5 text-black/60" strokeWidth={1.75} weight="regular" />
            </div>
            <div className="text-[13px] text-black/60">
              {q ? "No se encontraron resultados." : "Crea tu primer paciente."}
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            <table className="w-full text-sm" data-testid="patients-table">
              <thead className="sticky top-0 bg-[#F5F5F7] z-[1]">
                <tr className="text-left">
                  {[
                    "Paciente",
                    "Documento",
                    "Teléfono",
                    "Última visita",
                    !isSecretary && "Consulta",
                    "",
                  ]
                    .filter(Boolean)
                    .map((h) => (
                      <th
                        key={h}
                        className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold"
                      >
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    onClick={isSecretary ? undefined : () => nav(`${base}/${p.id}`)}
                    data-testid={`patient-row-${p.id}`}
                    className={`border-t border-black/[0.05] text-black transition group ${
                      isSecretary
                        ? ""
                        : "hover:bg-black/[0.02] cursor-pointer"
                    }`}
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        {p.photo_url ? (
                          <img
                            src={p.photo_url}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-[#F2F3F5] text-black text-[13px] font-bold flex items-center justify-center flex-shrink-0">
                            {initials(p.full_name)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold truncate max-w-[240px] text-[15px] text-black">
                            {p.full_name}
                          </div>
                          {p.email && (
                            <div className="text-[12px] text-black/50 truncate max-w-[240px]">
                              {p.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/80">
                      {p.document_id || "—"}
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/80">
                      {p.phone || "—"}
                    </td>
                    <td className="py-3 px-3 text-[13px] text-black/80 whitespace-nowrap">
                      {lastVisitById[p.id]
                        ? new Date(lastVisitById[p.id]).toLocaleDateString("es-ES", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                    {!isSecretary && (
                      <td
                        className="py-3 px-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => startConsultation(p)}
                          disabled={startingId === p.id}
                          data-testid={`patient-start-consult-${p.id}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-black hover:bg-black/85 text-white text-[11px] font-semibold transition disabled:opacity-60"
                        >
                          {startingId === p.id ? (
                            <span className="w-3 h-3 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Stethoscope className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                          )}
                          Iniciar
                        </button>
                      </td>
                    )}
                    <td className="py-3 pl-3 text-right whitespace-nowrap">
                      <div
                        className="inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActionsMenu
                          triggerTestId={`patient-actions-${p.id}`}
                          items={[
                            isSecretary && {
                              icon: Pencil,
                              label: "Editar paciente",
                              testId: `patient-edit-${p.id}`,
                              onClick: () => setEditing(p),
                            },
                            !isSecretary && {
                              icon: Trash2,
                              label: "Eliminar",
                              testId: `patient-delete-${p.id}`,
                              onClick: () => del(p.id),
                              color: "red",
                            },
                          ].filter(Boolean)}
                        />
                        {!isSecretary && (
                          <ChevronRight className="w-4 h-4 text-black/40 group-hover:translate-x-0.5 transition" strokeWidth={1.75} weight="regular" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditPatientModal
          patient={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <NewPatientModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => {
          load();
        }}
      />
    </div>
  );
}

// ─── KPI Widgets ──────────────────────────────────────────────────────────

function TotalDonut({ total, activos }) {
  const pct = total ? Math.round((activos / total) * 100) : 0;
  return (
    <AppleKpi
      testid="patients-kpi-total"
      icon={Users}
      title="Total pacientes"
      description="Panorama completo de tu cartera de pacientes."
      label="En cartera"
      value={total}
      footer={`${pct}% activos en los últimos 90 días`}
    />
  );
}

function ActivosSpectrum({ activos, total }) {
  const pct = total ? Math.round((activos / total) * 100) : 0;
  return (
    <AppleKpi
      testid="patients-kpi-activos"
      icon={CheckCircle2}
      title="Activos"
      description="Pacientes que han consultado en los últimos 90 días."
      label="Activos"
      value={activos}
      footer={`${pct}% del total`}
    />
  );
}

function ProgramadosCard({ programados, total }) {
  const pct = total ? Math.round((programados / total) * 100) : 0;
  return (
    <AppleKpi
      testid="patients-kpi-programados"
      icon={CalendarCheck}
      title="Con cita futura"
      description="Pacientes con una cita agendada próximamente."
      label="Programados"
      value={programados}
      footer={`${pct}% del total`}
    />
  );
}

function NuevosBars({ buckets, nuevos }) {
  return (
    <AppleKpi
      testid="patients-kpi-nuevos"
      icon={Sparkles}
      title="Nuevos"
      description="Pacientes registrados en los últimos 30 días."
      label="Este mes"
      value={nuevos}
      footer={nuevos === 0 ? "Sin ingresos nuevos aún" : "Comparado con los últimos 30 días"}
    />
  );
}
